import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { site, nav } from "../../data/content";
import { CTAButton } from "../common/CTAButton";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled ? "bg-navy/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                <div className="flex items-center justify-between h-[72px] lg:h-20">
                    <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
                        <img src={site.logo} alt="FRONTX logo" className="h-10 w-10 rounded-sm object-cover ring-1 ring-white/15" />
                        <div className="leading-none">
                            <span className="font-display font-extrabold text-white text-xl tracking-tight">FRONTX</span>
                            <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-slate-300 mt-0.5">Integrated Services</span>
                        </div>
                    </Link>

                    <nav className="hidden xl:flex items-center gap-5 2xl:gap-6">
                        {nav.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                                    pathname === n.to ? "text-brand" : "text-slate-200 hover:text-white"
                                }`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="hidden xl:flex items-center gap-3 shrink-0">
                        <CTAButton to="/contact#manpower" variant="outline" arrow={false} className="!px-4 !py-2.5 whitespace-nowrap" data-testid="nav-request-manpower">
                            Request Manpower
                        </CTAButton>
                        <CTAButton to="/contact#construction" variant="primary" arrow={false} className="!px-4 !py-2.5 whitespace-nowrap" data-testid="nav-project-quote">
                            Get a Quote
                        </CTAButton>
                    </div>

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="xl:hidden text-white p-2"
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden bg-navy border-t border-white/10 px-5 py-6" data-testid="nav-mobile-menu">
                    <nav className="flex flex-col gap-1">
                        {nav.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                className={`py-3 text-base font-medium border-b border-white/5 ${
                                    pathname === n.to ? "text-brand" : "text-slate-200"
                                }`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>
                    <div className="flex flex-col gap-3 mt-6">
                        <CTAButton to="/contact#manpower" variant="outline" arrow={false}>Request Manpower</CTAButton>
                        <CTAButton to="/contact#construction" variant="primary" arrow={false}>Get a Project Quote</CTAButton>
                    </div>
                </div>
            )}
        </header>
    );
};
